import { createApp } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import * as Sentry from "@sentry/vue";

import App from "./App.vue";
import LoginContents from "./components/LoginScreen.vue";

// Lazy-loaded components for better performance
const StartingDashboard = () => import("./components/StartDashboard.vue");
const FAQ = () => import("./components/FAQScreen.vue");
const RegisterIndividualDevice = () => import("./components/register/RegisterIndividualDevice.vue");
const RegisterIndividualUser = () => import("./components/register/RegisterIndividualUser.vue");
const EnrollDevice = () => import("./components/register/EnrollDevice.vue");
const RegisterDevice = () => import("./components/RegisterDevice.vue");
const DeviceList = () => import("./components/DeviceList.vue");
const DeviceDetails = () => import("./components/DeviceDetails.vue");
const LostMode = () => import("./components/LostMode.vue");
const DeviceLocation = () => import("./components/DeviceLocation.vue");
const UserProfile = () => import("./components/UserProfile.vue");
const EditAccount = () => import("./components/EditAccount.vue");
const ManageTemplates = () => import("./components/ManageTemplates.vue");
const ManageUserAdmin = () => import("./components/ManageUserAdmin.vue");
const DeviceReporting = () => import("./components/DeviceReporting.vue");
const DeviceReportingV2 = () => import("./components/DeviceReportingV2.vue");
const AuditTrail = () => import("./components/AuditTrail.vue");
const SettingsPage = () => import("./components/SettingsPage.vue");
const CreditDashboard = () => import("./components/credit_system/CreditDashboard.vue");
const TopupPage = () => import("./components/credit_system/TopupPage.vue");
const CreditStatus = () => import("./components/credit_system/CreditStatus.vue");
const CorpDeviceList = () => import("./components/CorpDeviceList.vue");
const SettlementPage = () => import("./components/credit_system/SettlementPage.vue");
const ABMConnection = () => import("./components/ABMConnection.vue");

import { loadClientConfig } from "./clientConfig";
import "./api/busyRetry"; // global 503 server_busy banner + one auto-retry (Issue 36)
import "./api/sessionGuard"; // realtime sign-out when the account is signed in elsewhere
import "./assets/theme.css";

import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.js";
import "bootstrap";

const app = createApp(App);

const routes = [
  {
    path: "/",
    components: {
      default: LoginContents,
    },
    params: true,
  },
  {
    path: "/dashboard",
    component: StartingDashboard,
  },
  {
    path: "/faq",
    component: FAQ,
  },
  {
    path: "/register/device",
    component: RegisterIndividualDevice,
  },
  {
    path: "/register/user",
    component: RegisterIndividualUser,
  },
  {
    path: "/enroll",
    component: EnrollDevice,
  },
  {
    path: "/register-devices",
    component: RegisterDevice,
  },
  {
    path: "/device-list",
    component: DeviceList,
  },
  {
    path: "/device-details",
    component: DeviceDetails,
  },
  {
    path: "/enterprise-device-details",
    component: CorpDeviceList,
  },
  {
    path: "/device-lost-mode",
    component: LostMode,
  },
  {
    path: "/device-location",
    component: DeviceLocation,
  },
  {
    path: "/profile",
    component: UserProfile,
  },
  {
    path: "/edit-account",
    component: EditAccount,
  },

  {
    path: "/manage-templates",
    component: ManageTemplates,
  },

  {
    path: "/manage-user-admin",
    component: ManageUserAdmin,
  },
  {
    path: "/reporting",
    component: DeviceReporting,
  },
  {
    path: "/reporting-v2",
    component: DeviceReportingV2,
  },
  // {
  //   path: "/executive-report",
  //   component: ExecutiveReport,
  // },
  {
    path: "/reporting/audit-trail",
    component: AuditTrail,
  },
  {
    path: "/settings",
    component: SettingsPage,
  },
  {
    path: "/credit-manager",
    component: CreditDashboard,
  },
  {
    path: "/credit-manager/settlement",
    component: SettlementPage,
  },
  {
    path: "/credit-manager/topup",
    component: TopupPage,
  },
  {
    path: "/credit-manager/status",
    component: CreditStatus,
    params: true,
  },
  {
    path: "/abm-connection",
    component: ABMConnection,
  },
];

// LOCAL-ONLY simulation pages. The src/sim/ folder is gitignored, so it only exists on
// developer machines — it is NOT in GitHub and therefore absent from staging/production
// builds. require.context tolerates the empty folder (.gitkeep), so this no-ops in prod.
try {
  // eslint-disable-next-line no-undef
  const simContext = require.context("./sim", false, /simRoutes\.js$/);
  simContext.keys().forEach((key) => {
    const mod = simContext(key);
    if (mod.routes) routes.push(...mod.routes);
    if (mod.navItem) window.__CTRLYTICS_SIM_NAV__ = mod.navItem;
    if (mod.footerItems) window.__CTRLYTICS_SIM_FOOTER__ = mod.footerItems;
  });
} catch (e) {
  // No simulation folder in this build (staging / production) — nothing to register.
}

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
});

// Self-heal stale deployments: a tab cached from an older build references
// old hashed chunk files, so the first lazy-route navigation after a deploy
// fails (ChunkLoadError / "Invalid route component" / webpack "reading 'call'")
// and the click silently does nothing. Reload once onto the intended route to
// pull the fresh build; the timestamp guard stops a reload loop if the error
// turns out not to be deploy-related, and every matched error is still sent
// to Sentry first so a reload never masks a real regression.
const STALE_CHUNK_ERROR_RE =
  /loading chunk|loading css chunk|invalid route component|reading 'call'|__vccOpts/i;
const CHUNK_RELOAD_COOLDOWN_MS = 10000;
router.onError((error, to) => {
  try {
    const message = (error && error.message) || "";
    const isStaleChunk =
      (error && error.name === "ChunkLoadError") || STALE_CHUNK_ERROR_RE.test(message);
    if (!isStaleChunk) return;
    Sentry.captureException(error, { tags: { stale_chunk_reload: true } });
    const targetPath = to && to.fullPath ? to.fullPath : window.location.pathname;
    const guardKey = "ctrlytics_chunk_reload:" + targetPath;
    // If sessionStorage is unavailable (managed-browser policy), skip the
    // reload entirely — no cooldown tracking means a loop would be possible.
    const lastReloadAt = Number(sessionStorage.getItem(guardKey) || 0);
    if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(guardKey, String(Date.now()));
    window.location.assign(targetPath);
  } catch (handlerError) {
    // Never let the recovery path itself throw inside vue-router's
    // error-handler loop; behaving like the pre-fix "stuck page" is the
    // safe fallback.
    Sentry.captureException(handlerError);
  }
});
Sentry.init({
  app,
  dsn: "https://95c61ae1733fcecc4c7b04cf43babd81@o4505485535346688.ingest.us.sentry.io/4506620488908800",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: process.env.NODE_ENV === "production" ? 0.2 : 0.0, //  Disable tracing in dev, 20% in prod
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export default router;
loadClientConfig().then(() => {
  app.use(router).mount("#app");
});
